/**
 * Current Stock Filters Component
 * @see US-12b.4 - Page Liste Stock
 * @see Sprint 27 - Stock a Date
 */

import { Input, Select, DatePicker, parseDateParam, formatDateParam, type DateRange } from '@/components/ui';
import { useFlatCategories } from '@/features/raw-products/hooks/useCategories';
import { MarketTypeLabels } from '@bstock/shared';

interface CurrentStockFiltersProps {
  search: string;
  categoryId: string;
  marketType: string;
  lowStockOnly: boolean;
  /** Date au format YYYY-MM-DD ou YYYY-MM-DDTHH:mm */
  date: string | null;
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: string) => void;
  onMarketTypeChange: (value: string) => void;
  onLowStockOnlyChange: (value: boolean) => void;
  onDateChange: (value: string | null) => void;
}

export function CurrentStockFilters({
  search,
  categoryId,
  marketType,
  lowStockOnly,
  date,
  onSearchChange,
  onCategoryChange,
  onMarketTypeChange,
  onLowStockOnlyChange,
  onDateChange,
}: CurrentStockFiltersProps) {
  // Fetch categories for filter
  const { data: categories = [] } = useFlatCategories();

  // Category options
  const categoryOptions = [
    { value: '', label: 'Toutes les categories' },
    ...categories.map((c: { id: string; name: string }) => ({ value: c.id, label: c.name })),
  ];

  // Market type options
  const marketTypeOptions = [
    { value: '', label: 'Tous les types' },
    ...Object.entries(MarketTypeLabels).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  // Low stock filter options
  const stockFilterOptions = [
    { value: 'all', label: 'Tous les stocks' },
    { value: 'low', label: 'Stock faible uniquement' },
  ];

  // Convert string date to DateRange for the picker (only use start, ignore end)
  const dateValue: DateRange = {
    start: parseDateParam(date),
    end: parseDateParam(date), // Set same as start for display
  };

  // Handle date change - only use start date, preserving time if set
  const handleDateChange = (range: DateRange) => {
    onDateChange(formatDateParam(range.start));
  };

  return (
    <div className="flex flex-wrap items-end gap-4">
      {/* Date filter (Sprint 27) - Au debut de la row */}
      <div className="w-64">
        <DatePicker
          asSingle
          showTime
          value={dateValue}
          onChange={handleDateChange}
          placeholder="Selectionner une date"
        />
      </div>

      {/* Search */}
      <div className="w-64">
        <Input
          placeholder="Rechercher par nom ou SKU..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      {/* Category filter */}
      <div className="w-48">
        <Select
          value={categoryId}
          onChange={onCategoryChange}
          options={categoryOptions}
          placeholder="Categorie"
          allowEmpty={false}
        />
      </div>

      {/* Market type filter */}
      <div className="w-40">
        <Select
          value={marketType}
          onChange={onMarketTypeChange}
          options={marketTypeOptions}
          placeholder="Type"
          allowEmpty={false}
        />
      </div>

      {/* Low stock filter */}
      <div className="w-48">
        <Select
          value={lowStockOnly ? 'low' : 'all'}
          onChange={(v) => onLowStockOnlyChange(v === 'low')}
          options={stockFilterOptions}
          placeholder="Stock"
          allowEmpty={false}
        />
      </div>
    </div>
  );
}
